import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Edit3, Trash2, Copy, Search, RotateCcw, Save, X, Check,
  Shield, Eye, EyeOff, Users, Lock, ChevronDown, ChevronRight,
  AlertTriangle, Sparkles, PlayCircle, FileDown,
} from "lucide-react";
import { usePermissions } from "@/contexts/PermissionsContext";

const ROLE_PRESET_COLORS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
  "#10b981", "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1", "#a855f7",
  "#d946ef", "#ec4899", "#64748b", "#94a3b8",
];

/**
 * RolesEditor (Phase 5A) — Kodlogy-style + smarter
 *  • Top: Role-management table with description, visibility, user-count, edit/duplicate/delete
 *  • Bottom: Module × Action editor with search, grouping, live diff
 */
export default function RolesEditor() {
  const { setPreviewRole, isAdmin, previewRole, canManageRoles } = usePermissions();
  const [perms, setPerms] = useState(null);          // GET /settings/role-permissions response
  const [rolesMeta, setRolesMeta] = useState([]);    // GET /settings/roles
  const [draft, setDraft] = useState(null);
  const [selectedRole, setSelectedRole] = useState("viewer");
  const [search, setSearch] = useState("");
  const [openGroups, setOpenGroups] = useState({});  // { groupKey: bool }
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);      // role meta currently in modal (or {} for new)

  async function loadAll() {
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        api.get("/settings/role-permissions"),
        api.get("/settings/roles"),
      ]);
      setPerms(p);
      setDraft(p.matrix);
      setRolesMeta(r.roles || []);
      // expand all groups by default
      const init = {};
      (p.module_groups || []).forEach((g) => { init[g.key] = true; });
      setOpenGroups(init);
    } catch {
      toast.error("Konnte Rollen-Daten nicht laden");
    }
  }
  useEffect(() => { loadAll(); }, []);

  if (!perms || !draft) return <div className="text-zinc-500 text-sm p-4">Lade…</div>;

  const dirty = JSON.stringify(perms.matrix) !== JSON.stringify(draft);
  const diffCount = countDiffs(perms.matrix, draft);
  const cur = draft[selectedRole] || {};
  const selectedMeta = rolesMeta.find((r) => r.key === selectedRole);

  function toggle(role, module, action) {
    if (!canManageRoles) return;
    const next = JSON.parse(JSON.stringify(draft));
    const v = !next[role][module][action];
    next[role][module][action] = v;
    if (action === "view" && !v) perms.actions.forEach((a) => { next[role][module][a] = false; });
    if (action !== "view" && v) next[role][module]["view"] = true;
    setDraft(next);
  }
  function setRowAll(role, module, val) {
    if (!canManageRoles) return;
    const next = JSON.parse(JSON.stringify(draft));
    perms.actions.forEach((a) => { next[role][module][a] = val; });
    setDraft(next);
  }
  function setGroupAll(role, modules, val) {
    if (!canManageRoles) return;
    const next = JSON.parse(JSON.stringify(draft));
    modules.forEach((m) => { perms.actions.forEach((a) => { next[role][m][a] = val; }); });
    setDraft(next);
  }
  async function save() {
    setSaving(true);
    try {
      // Phase A — never send admin matrix to the backend; it's read-only.
      const payload = { ...draft };
      delete payload.admin;
      const { data: fresh } = await api.put("/settings/role-permissions", { matrix: payload });
      setPerms(fresh);
      setDraft(fresh.matrix);
      toast.success("Berechtigungen gespeichert");
      window.dispatchEvent(new CustomEvent("permissions-updated"));
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  }
  async function reset() {
    if (!window.confirm("Alle Berechtigungen auf Standardwerte zurücksetzen?")) return;
    setSaving(true);
    try {
      const { data: fresh } = await api.post("/settings/role-permissions/reset");
      setPerms(fresh);
      setDraft(fresh.matrix);
      toast.success("Auf Standard zurückgesetzt");
      window.dispatchEvent(new CustomEvent("permissions-updated"));
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  }
  function discard() {
    setDraft(perms.matrix);
    toast.info("Änderungen verworfen");
  }
  async function downloadCockpit() {
    try {
      const res = await api.get("/settings/role-permissions/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Berechtigungs-Cockpit_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Cockpit-PDF heruntergeladen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
    }
  }
  async function copyFrom(sourceKey) {
    if (!sourceKey || sourceKey === selectedRole) return;
    if (!window.confirm(`Berechtigungen von „${labelFor(sourceKey)}" auf „${labelFor(selectedRole)}" übernehmen? Aktuelle Einstellungen werden überschrieben.`)) return;
    const next = JSON.parse(JSON.stringify(draft));
    next[selectedRole] = JSON.parse(JSON.stringify(next[sourceKey]));
    setDraft(next);
    toast.success(`Kopiert von „${labelFor(sourceKey)}"`);
  }

  function labelFor(key) {
    const r = rolesMeta.find((x) => x.key === key);
    return r?.label || key;
  }

  // -------- Role CRUD via modal --------
  async function saveRoleMeta(payload, isNew) {
    try {
      if (isNew) {
        await api.post("/settings/roles", payload);
        toast.success(`Rolle „${payload.label}" erstellt`);
      } else {
        await api.put(`/settings/roles/${payload.key}`, payload);
        toast.success("Rolle aktualisiert");
      }
      setEditing(null);
      await loadAll();
      if (isNew) setSelectedRole(payload.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  }
  async function deleteRole(key) {
    const meta = rolesMeta.find((r) => r.key === key);
    if (!meta) return;
    if (meta.user_count > 0) {
      toast.error(`Rolle ist ${meta.user_count} Benutzern zugewiesen — bitte zuerst neu zuweisen`);
      return;
    }
    const PROTECTED = new Set(["admin", "fahrer"]);
    if (PROTECTED.has(meta.key)) {
      toast.error(`„${meta.label}" ist eine Kern-Rolle und kann nicht entfernt werden`);
      return;
    }
    const msg = meta.is_system
      ? `Rolle „${meta.label}" ausblenden?\n\nSystem-Rollen werden nur soft-versteckt — die Berechtigungs-Matrix bleibt intakt und kann via Datenbank wiederhergestellt werden.`
      : `Rolle „${meta.label}" wirklich löschen?`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/settings/roles/${key}`);
      toast.success(meta.is_system ? "Rolle ausgeblendet" : "Rolle gelöscht");
      if (selectedRole === key) setSelectedRole("fahrer");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  // ---- Filter modules by search ----
  const searchLower = search.trim().toLowerCase();
  const moduleMatches = (m) => {
    if (!searchLower) return true;
    const lbl = (perms.module_labels[m] || m).toLowerCase();
    return lbl.includes(searchLower) || m.toLowerCase().includes(searchLower);
  };
  const groupHasMatches = (group) => group.modules.some(moduleMatches);

  return (
    <div className="space-y-5" data-testid="roles-editor">
      {/* ============ TOP: Role-Management Table ============ */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display font-semibold text-zinc-50 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              Benutzerrollen
            </h3>
            <p className="text-xs text-zinc-500 mt-0.5">Verwaltung aller Rollen mit Beschreibung, Sichtbarkeit und Berechtigungen.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadCockpit}
              data-testid="perm-cockpit-pdf-overview"
              title="Berechtigungs-Cockpit als A3-PDF (Compliance-Übersicht aller Rollen × Module × Aktionen)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-amber-600/80 text-zinc-200 text-xs font-medium rounded">
              <FileDown className="w-4 h-4" /> Cockpit-PDF
            </button>
            {canManageRoles && (
              <button
                onClick={() => setEditing({ __new: true, label: "", description: "", color: "#3b82f6", is_visible_to_employees: true, copy_from: "fahrer" })}
                data-testid="role-add-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded">
                <Plus className="w-4 h-4" /> Neue Rolle
              </button>
            )}
            {!canManageRoles && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-amber-300 bg-amber-500/5 border border-amber-500/20 px-2.5 py-1.5 rounded" data-testid="roles-readonly-badge">
                <Lock className="w-3 h-3" /> Nur Lesezugriff — nur Admin oder Geschäftsführung dürfen Rollen ändern
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <th className="px-5 py-3 text-left font-semibold">Rolle</th>
                <th className="px-3 py-3 text-left font-semibold">Beschreibung</th>
                <th className="px-3 py-3 text-center font-semibold w-20">Benutzer</th>
                <th className="px-3 py-3 text-center font-semibold w-28">Module</th>
                <th className="px-3 py-3 text-center font-semibold w-32">Sichtbar f. Mitarbeiter</th>
                <th className="px-3 py-3 text-right font-semibold w-32">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rolesMeta.map((r, idx) => {
                const isSelected = selectedRole === r.key;
                return (
                  <tr key={r.key}
                    onClick={() => setSelectedRole(r.key)}
                    data-testid={`role-row-${r.key}`}
                    className={`cursor-pointer border-b border-zinc-800/60 transition-colors ${
                      isSelected ? "bg-blue-500/5" : idx % 2 === 0 ? "bg-zinc-900/40" : "bg-zinc-900"
                    } hover:bg-blue-500/10`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: r.color }} />
                        <span className="text-zinc-100 font-medium">{r.label}</span>
                        {r.is_system ? (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700">System</span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">Custom</span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-600 font-mono mt-0.5">{r.key}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-zinc-400 max-w-md">
                      {r.description || <span className="text-zinc-600 italic">Keine Beschreibung</span>}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center gap-1 text-xs font-mono">
                        <Users className="w-3 h-3 text-zinc-500" />
                        <span className={r.user_count > 0 ? "text-zinc-200" : "text-zinc-600"}>{r.user_count}</span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-zinc-800 text-zinc-300">
                        {r.modules_visible}/{r.modules_total}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {r.is_visible_to_employees ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><Eye className="w-3 h-3" /> sichtbar</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-zinc-500 text-xs"><EyeOff className="w-3 h-3" /> verborgen</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                        {isAdmin && r.key !== "admin" && (
                          <button onClick={() => { setPreviewRole(r.key); toast.success(`Vorschau als „${r.label}" aktiviert`); }}
                            data-testid={`role-preview-${r.key}`}
                            className={`p-1.5 rounded ${previewRole === r.key ? "text-amber-400 bg-amber-500/15" : "text-zinc-400 hover:text-amber-400 hover:bg-amber-500/10"}`}
                            title={previewRole === r.key ? "Vorschau aktiv" : "Als diese Rolle ansehen"}>
                            <PlayCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canManageRoles && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedRole(r.key); setTimeout(() => { document.getElementById("perm-matrix-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50); }}
                              data-testid={`role-edit-perms-${r.key}`}
                              className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-blue-300 bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 rounded inline-flex items-center gap-1"
                              title="Berechtigungs-Matrix für diese Rolle bearbeiten"
                            >
                              <Shield className="w-3 h-3" />
                              Berechtigungen
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditing({ ...r }); }}
                              data-testid={`role-edit-${r.key}`}
                              className="p-1.5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 rounded" title="Meta (Label, Farbe, Beschreibung) bearbeiten">
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setEditing({ __new: true, label: `${r.label} (Kopie)`, description: r.description, color: r.color, is_visible_to_employees: r.is_visible_to_employees, copy_from: r.key }); }}
                              data-testid={`role-duplicate-${r.key}`}
                              className="p-1.5 text-zinc-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded" title="Duplizieren">
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); deleteRole(r.key); }}
                              disabled={["admin", "fahrer"].includes(r.key) || r.user_count > 0}
                              data-testid={`role-delete-${r.key}`}
                              className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                              title={
                                ["admin", "fahrer"].includes(r.key) ? "Kern-Rolle (geschützt)" :
                                r.user_count > 0 ? "In Benutzung" :
                                r.is_system ? "Ausblenden (soft-hide)" : "Löschen"
                              }>
                              {["admin", "fahrer"].includes(r.key) ? <Lock className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============ BOTTOM: Permission editor for selected role ============ */}
      <div id="perm-matrix-anchor" className="bg-zinc-900 border border-zinc-800 rounded-md scroll-mt-4">
        {/* Sticky header — selected role + actions */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap sticky top-0 z-10 bg-zinc-900">
          <div className="flex items-center gap-3">
            {selectedMeta && <span className="w-3 h-3 rounded-full" style={{ background: selectedMeta.color }} />}
            <div>
              <h4 className="font-display font-semibold text-zinc-100 flex items-center gap-2">
                Zugriffsrechte: <span className="text-blue-400">{labelFor(selectedRole)}</span>
                {dirty && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {diffCount} Änderung{diffCount !== 1 ? "en" : ""}
                  </span>
                )}
              </h4>
              {selectedMeta?.description && <p className="text-xs text-zinc-500 mt-0.5 max-w-xl">{selectedMeta.description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Copy from another role */}
            {canManageRoles && (
              <select onChange={(e) => { copyFrom(e.target.value); e.target.value = ""; }}
                data-testid="perm-copy-from"
                defaultValue=""
                className="bg-zinc-950 border border-zinc-800 rounded px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-blue-500">
                <option value="" disabled>Kopieren von …</option>
                {rolesMeta.filter((r) => r.key !== selectedRole).map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            )}
            {dirty && canManageRoles && (
              <button onClick={discard} data-testid="perm-discard"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">
                <X className="w-3.5 h-3.5" /> Verwerfen
              </button>
            )}
            <button onClick={downloadCockpit} disabled={saving} data-testid="perm-cockpit-pdf"
              title="Berechtigungs-Cockpit als A3-PDF (Compliance-Übersicht)"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-amber-600/80 text-zinc-200 rounded">
              <FileDown className="w-3.5 h-3.5" /> Cockpit-PDF
            </button>
            {canManageRoles && (
              <>
                <button onClick={reset} disabled={saving} data-testid="perm-reset"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-red-600/80 text-zinc-200 rounded">
                  <RotateCcw className="w-3.5 h-3.5" /> Standard
                </button>
                <button onClick={save} disabled={!dirty || saving} data-testid="perm-save"
                  className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded font-medium">
                  <Save className="w-3.5 h-3.5" /> {saving ? "Speichere…" : "Speichern"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Modul suchen … (z.B. Mitarbeiter, Finanzen)"
            data-testid="perm-search"
            className="flex-1 bg-transparent text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
          />
          {search && <button onClick={() => setSearch("")} className="text-zinc-500 hover:text-zinc-300 text-xs">×</button>}
        </div>

        {/* Admin notice */}
        {selectedRole === "admin" && (
          <div className="mx-5 mt-4 p-3 rounded bg-blue-500/5 border border-blue-500/20 text-xs text-blue-300 flex items-start gap-2">
            <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span><strong>Admin</strong> hat immer Vollzugriff und kann nicht eingeschränkt werden.</span>
          </div>
        )}

        {/* Group → Module × Action grid */}
        <div className="divide-y divide-zinc-800">
          {(perms.module_groups || []).filter(groupHasMatches).map((group) => (
            <div key={group.key}>
              <div className="px-5 py-2.5 bg-zinc-900/60 flex items-center justify-between gap-2">
                <button
                  onClick={() => setOpenGroups((g) => ({ ...g, [group.key]: !g[group.key] }))}
                  className="flex items-center gap-2 text-zinc-200 hover:text-white text-sm font-medium"
                  data-testid={`perm-group-${group.key}`}>
                  {openGroups[group.key] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <span className="uppercase tracking-wider text-[11px]">{group.label}</span>
                  <span className="text-[10px] text-zinc-500 font-mono">{group.modules.filter(moduleMatches).length}</span>
                </button>
                {selectedRole !== "admin" && (
                  <div className="flex gap-1">
                    <button onClick={() => setGroupAll(selectedRole, group.modules, true)}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded border border-emerald-500/30">
                      Alle an
                    </button>
                    <button onClick={() => setGroupAll(selectedRole, group.modules, false)}
                      className="text-[10px] uppercase tracking-wider px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded border border-zinc-700">
                      Alle aus
                    </button>
                  </div>
                )}
              </div>
              {openGroups[group.key] && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="px-5 py-2 text-left font-semibold w-1/3">Modul</th>
                      {perms.actions.map((a) => (
                        <th key={a} className="px-3 py-2 text-center font-semibold w-20">{perms.action_labels[a] || a}</th>
                      ))}
                      <th className="px-3 py-2 text-center font-semibold w-20">Zeile</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.modules.filter(moduleMatches).map((mod, idx) => (
                      <tr key={mod} className={`border-t border-zinc-800/60 ${idx % 2 === 0 ? "bg-zinc-900/30" : ""}`}>
                        <td className="px-5 py-2.5">
                          <div className="text-zinc-100 font-medium">{perms.module_labels[mod] || mod}</div>
                          <div className="text-[10px] text-zinc-600 font-mono">{mod}</div>
                        </td>
                        {perms.actions.map((a) => {
                          const checked = !!cur[mod]?.[a];
                          const changed = !!perms.matrix[selectedRole]?.[mod]?.[a] !== checked;
                          return (
                            <td key={a} className="px-3 py-2.5 text-center">
                              <label className={`inline-flex items-center justify-center cursor-pointer ${changed ? "ring-2 ring-amber-500/40 rounded" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggle(selectedRole, mod, a)}
                                  data-testid={`perm-${selectedRole}-${mod}-${a}`}
                                  className="w-4 h-4 rounded accent-blue-600"
                                  disabled={selectedRole === "admin"}
                                />
                              </label>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2.5 text-center">
                          <div className="inline-flex gap-1">
                            <button onClick={() => setRowAll(selectedRole, mod, true)} disabled={selectedRole === "admin"}
                              className="text-[10px] px-1.5 py-0.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded border border-emerald-500/30 disabled:opacity-30">
                              An
                            </button>
                            <button onClick={() => setRowAll(selectedRole, mod, false)} disabled={selectedRole === "admin"}
                              className="text-[10px] px-1.5 py-0.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded border border-zinc-700 disabled:opacity-30">
                              Aus
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-zinc-800 text-[11px] text-zinc-500 leading-relaxed flex items-start gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
          <span>
            <strong className="text-zinc-300">Smart-Logik:</strong> „Ansehen" wird automatisch aktiviert, sobald eine andere Aktion gesetzt wird.
            Beim Ausschalten von „Ansehen" werden alle anderen Aktionen ebenfalls deaktiviert. Geänderte Felder sind <span className="text-amber-300">amber umrandet</span>.
            Diese Matrix steuert UI-Sichtbarkeit (Sidebar, Buttons) und API-Berechtigungen.
          </span>
        </div>
        {perms.updated_at && (
          <div className="px-5 py-2 border-t border-zinc-800 text-[10px] text-zinc-600">
            Zuletzt geändert: {new Date(perms.updated_at).toLocaleString("de-AT")} {perms.updated_by && `· ${perms.updated_by}`}
          </div>
        )}
      </div>

      {/* ============ Role-Edit Modal ============ */}
      {editing && (
        <RoleEditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={saveRoleMeta}
          rolesMeta={rolesMeta}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------
function countDiffs(a, b) {
  let n = 0;
  for (const role of Object.keys(b || {})) {
    for (const mod of Object.keys(b[role] || {})) {
      for (const act of Object.keys(b[role][mod] || {})) {
        if (!!a?.[role]?.[mod]?.[act] !== !!b[role][mod][act]) n++;
      }
    }
  }
  return n;
}

// --------------------------------------------------------------------------
// Role-Edit Modal
// --------------------------------------------------------------------------
function RoleEditModal({ initial, onClose, onSave, rolesMeta }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    key: initial.key || "",
    label: initial.label || "",
    description: initial.description || "",
    color: initial.color || "#3b82f6",
    is_visible_to_employees: initial.is_visible_to_employees ?? true,
    copy_from: initial.copy_from || "",
  });
  const [saving, setSaving] = useState(false);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit() {
    if (!form.label.trim()) { toast.error("Bezeichnung ist erforderlich"); return; }
    setSaving(true);
    try {
      const payload = isNew
        ? { label: form.label, description: form.description, color: form.color, is_visible_to_employees: form.is_visible_to_employees, copy_from: form.copy_from || undefined }
        : { key: form.key, label: form.label, description: form.description, color: form.color, is_visible_to_employees: form.is_visible_to_employees };
      await onSave(payload, isNew);
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="role-edit-modal">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="font-display font-semibold text-zinc-100">{isNew ? "Neue Rolle anlegen" : `Rolle bearbeiten: ${initial.label}`}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Bezeichnung *</label>
            <input value={form.label} onChange={(e) => update("label", e.target.value)} disabled={!isNew && initial.is_system}
              data-testid="role-modal-label"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-blue-500 disabled:opacity-60" />
            {!isNew && initial.is_system && <p className="text-[10px] text-zinc-600 mt-1">System-Bezeichnung kann nicht geändert werden, andere Felder schon.</p>}
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Beschreibung</label>
            <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={3}
              data-testid="role-modal-description"
              placeholder="Wofür ist diese Rolle? Was macht jemand mit dieser Rolle?"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-blue-500" />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Farbe</label>
            <div className="flex flex-wrap gap-1.5">
              {ROLE_PRESET_COLORS.map((c) => (
                <button key={c} onClick={() => update("color", c)}
                  className={`w-7 h-7 rounded transition ${form.color === c ? "ring-2 ring-white scale-110" : "ring-1 ring-zinc-800 hover:scale-110"}`}
                  style={{ background: c }} title={c} />
              ))}
              <input type="color" value={form.color} onChange={(e) => update("color", e.target.value)}
                className="w-7 h-7 rounded cursor-pointer bg-transparent" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-zinc-200 cursor-pointer">
            <input type="checkbox" checked={form.is_visible_to_employees} onChange={(e) => update("is_visible_to_employees", e.target.checked)}
              data-testid="role-modal-visibility"
              className="w-4 h-4 rounded accent-blue-600" />
            <span>Im Benutzer-Anlegen-Dropdown sichtbar</span>
          </label>

          {isNew && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Berechtigungen kopieren von</label>
              <select value={form.copy_from} onChange={(e) => update("copy_from", e.target.value)}
                data-testid="role-modal-copyfrom"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-blue-500">
                <option value="">– Keine (alles deaktiviert) –</option>
                {rolesMeta.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <p className="text-[10px] text-zinc-600 mt-1">Empfohlen: starte von einer ähnlichen Rolle und passe sie an.</p>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="role-modal-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded">
            <Check className="w-3.5 h-3.5" /> {saving ? "Speichere…" : isNew ? "Anlegen" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
